'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { RootLoading } from '@workspace/ui';
import {
  isGuestOnlyRoute,
  isPublicRoute,
  subscribeToAuthTokenRefresh,
  subscribeToCrossAppAuthRefresh,
} from '@workspace/ui/lib';
import { useSessionExtension } from '@workspace/ui/hooks';
import type { JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import {
  clearAuthCookies,
  refreshToken,
  syncAuthSessionFromCookies,
  syncAuthStateFromAccessToken,
} from '@/actions/mixed/auth';
import { GUEST_ONLY_ROUTES, PUBLIC_ROUTES } from '@/config/routes';
import { useAuthStore } from '@/stores/authStore';

const ONBOARDING_CHECK_KEY = 'onboarding_check_completed';

export default function AuthBootstrap({
  initialAccessToken,
  hasRefreshToken,
  children,
}: {
  initialAccessToken?: string;
  hasRefreshToken: boolean;
  children: React.ReactNode;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setIsAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setIsLoading = useAuthStore((s) => s.setIsLoading);
  const setUser = useAuthStore((s) => s.setUser);
  const isLoading = useAuthStore((s) => s.isLoading);

  const _router = useRouter();
  const pathname = usePathname();
  useSessionExtension({
    isEnabled: hasRefreshToken && !isGuestOnlyRoute(GUEST_ONLY_ROUTES, pathname),
    sessionVersion: accessToken,
    refreshSession: refreshToken,
  });

  useEffect(() => subscribeToCrossAppAuthRefresh(syncAuthSessionFromCookies), []);

  useEffect(
    () =>
      subscribeToAuthTokenRefresh((nextAccessToken) => {
        try {
          syncAuthStateFromAccessToken(nextAccessToken);
        } catch (error) {
          console.error('Failed to synchronize refreshed access token:', error);
        }
      }),
    []
  );

  useEffect(() => {
    // Don't trigger refresh for guest-only routes or when we have an access token
    if (isGuestOnlyRoute(GUEST_ONLY_ROUTES, pathname) || accessToken) {
      setIsLoading(false);
      return;
    }

    if (isPublicRoute(PUBLIC_ROUTES, pathname) && !initialAccessToken && !hasRefreshToken) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const now = Date.now();
    const expireSession = () => {
      if (abortController.signal.aborted) return;

      localStorage.removeItem(ONBOARDING_CHECK_KEY);
      setAccessToken(null);
      setIsAuthenticated(false);
      setUser(null);
      void clearAuthCookies()
        .catch(() => undefined)
        .finally(() => {
          window.location.href = '/login';
        });
    };
    const refreshSession = () => {
      void refreshToken()
        .then((result) => {
          if (abortController.signal.aborted) return;
          if (!result.success && !result.isProcessing) expireSession();
        })
        .catch((error) => {
          console.error('Failed to refresh token:', error);
          expireSession();
        })
        .finally(() => {
          if (!abortController.signal.aborted) setIsLoading(false);
        });
    };

    // If we have an initial token, decode it to check expiration
    if (initialAccessToken) {
      try {
        const decodedToken = jwtDecode<JwtPayload>(initialAccessToken);

        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds

        const user: JwtUser = {
          user: {
            id: Number(decodedToken.sub),
            first_name: decodedToken.user.first_name,
            last_name: decodedToken.user.last_name,
            email: decodedToken.user.email,
          },
          permissions: decodedToken.permissions,
          roles: decodedToken.roles,
          organisations: decodedToken.organisations ?? [],
          active_organisation: decodedToken.active_organisation ?? decodedToken.organisations?.[0],
          accessible_app: decodedToken.accessible_app,
          teams: decodedToken.teams,
        };

        // Only refresh if we're within 1 minute of expiration
        if (expirationTime - now > 60 * 1000) {
          if (abortController.signal.aborted) return;

          setAccessToken(initialAccessToken);

          setIsAuthenticated(true);

          setUser(user);
          setIsLoading(false);

          return;
        } else {
          refreshSession();
        }
      } catch (error) {
        console.error('Failed to decode token:', error);

        if (hasRefreshToken) refreshSession();
        else expireSession();
      }
    } else if (hasRefreshToken) {
      refreshSession();
    } else {
      setIsLoading(false);
    }

    return () => {
      abortController.abort();
    };
  }, [
    pathname,
    accessToken,
    hasRefreshToken,
    setAccessToken,
    setIsAuthenticated,
    setIsLoading,
    setUser,
    initialAccessToken,
  ]);

  if (isLoading) {
    return <RootLoading />;
  }

  return children;
}
